import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Shield, LayoutDashboard, ChevronDown, Sparkles, Zap, 
  Users, Building2, Crown, TestTube, RefreshCw 
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getErrorMessage } from '../../utils/errorHandler';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_INFO = {
  free: { icon: Zap, color: 'bg-gray-500', label: 'Free' },
  starter: { icon: Sparkles, color: 'bg-blue-500', label: 'Starter' },
  pro: { icon: Users, color: 'bg-amber-500', label: 'Pro' },
  ministry: { icon: Building2, color: 'bg-purple-500', label: 'Ministry' },
  enterprise: { icon: Crown, color: 'bg-gradient-to-r from-amber-400 to-amber-600', label: 'Enterprise' }
};

export default function AdminQuickSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminRole, setAdminRole] = useState(null);
  const [simulatedTier, setSimulatedTier] = useState(null);
  const [loading, setLoading] = useState(false);

  const isOnAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    checkAdminStatus();
    checkSimulatedTier();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setIsAdmin(true);
      // Get admin role
      const adminsRes = await axios.get(`${API}/admin/admins`);
      const currentAdmin = adminsRes.data.admins.find(a => a.user_id === user?.user_id);
      setAdminRole(currentAdmin?.role || 'admin');
    } catch {
      setIsAdmin(false);
    }
  };

  const checkSimulatedTier = async () => {
    try {
      const response = await axios.get(`${API}/admin/simulated-tier`);
      setSimulatedTier(response.data.simulated_tier);
    } catch {
      setSimulatedTier(null);
    }
  };

  const handleSimulateTier = async (tier) => {
    setLoading(true);
    try {
      if (tier === 'reset') {
        await axios.delete(`${API}/admin/simulated-tier`);
        setSimulatedTier(null);
        toast.success(t('tier_simulation_cleared') || 'Tier simulation cleared - using actual subscription');
      } else {
        await axios.post(`${API}/admin/simulated-tier`, { tier });
        setSimulatedTier(tier);
        toast.success(`${t('now_simulating') || 'Now simulating'}: ${TIER_INFO[tier]?.label || tier} tier`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to change tier simulation'));
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) return null;

  const TierIcon = simulatedTier ? TIER_INFO[simulatedTier]?.icon : null;

  return (
    <div className="fixed bottom-20 right-4 z-50 flex items-center gap-2">
      {/* Simulated Tier Indicator */}
      <AnimatePresence>
        {simulatedTier && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
          >
            <Badge 
              className={`${TIER_INFO[simulatedTier]?.color} text-white flex items-center gap-1 py-1 px-3`}
              data-testid="simulated-tier-badge"
            >
              <TestTube className="w-3 h-3" />
              {t('testing') || 'Testing'}: {TIER_INFO[simulatedTier]?.label}
            </Badge>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quick Switcher Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            size="sm"
            className="bg-slate-800 hover:bg-slate-700 text-white shadow-lg gap-2"
            data-testid="admin-quick-switcher-btn"
          >
            <Shield className="w-4 h-4" />
            {t('admin') || 'Admin'}
            <ChevronDown className="w-3 h-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {/* Dashboard Switch */}
          <DropdownMenuLabel className="flex items-center gap-2">
            <LayoutDashboard className="w-4 h-4" />
            {t('quick_navigation') || 'Quick Navigation'}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {isOnAdminPage ? (
            <DropdownMenuItem 
              onClick={() => navigate('/dashboard')}
              data-testid="switch-to-app-dashboard"
            >
              <LayoutDashboard className="w-4 h-4 mr-2" />
              {t('go_to_app_dashboard') || 'Go to App Dashboard'}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem 
              onClick={() => navigate('/admin')}
              data-testid="switch-to-admin-dashboard"
            >
              <Shield className="w-4 h-4 mr-2" />
              {t('go_to_admin_dashboard') || 'Go to Admin Dashboard'}
            </DropdownMenuItem>
          )}
          
          {/* Tier Simulation - Only for owner/admin */}
          {(adminRole === 'owner' || adminRole === 'admin') && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="flex items-center gap-2">
                <TestTube className="w-4 h-4" />
                {t('tier_simulation') || 'Tier Simulation'}
              </DropdownMenuLabel>
              
              {Object.entries(TIER_INFO).map(([tier, info]) => {
                const Icon = info.icon;
                const isActive = simulatedTier === tier;
                return (
                  <DropdownMenuItem 
                    key={tier}
                    onClick={() => handleSimulateTier(tier)}
                    disabled={loading}
                    className={isActive ? 'bg-amber-50 dark:bg-amber-900/20' : ''}
                    data-testid={`simulate-tier-${tier}`}
                  >
                    <Icon className={`w-4 h-4 mr-2 ${isActive ? 'text-amber-500' : ''}`} />
                    {info.label}
                    {isActive && <Badge className="ml-auto bg-amber-500 text-xs">{t('active') || 'Active'}</Badge>}
                  </DropdownMenuItem>
                );
              })}
              
              {simulatedTier && (
                <DropdownMenuItem 
                  onClick={() => handleSimulateTier('reset')}
                  disabled={loading}
                  className="text-red-600"
                  data-testid="reset-tier-simulation"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('reset_to_actual_tier') || 'Reset to Actual Tier'}
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
